import { APPS_RAIL_COOKIE } from '~/shared/utils/cookies';

/**
 * `/apps/*` LEFT RAIL — the pure geometry + persistence VOCABULARY.
 *
 * 🔴 DEPENDENCY-FREE ON PURPOSE. Splitting these constants out of `appsRailState.tsx`
 * keeps React (and, through `~/hooks/useSubnavBottom`, the scroll-area context) off the
 * path of any node-tier importer. The React half re-exports everything here, so a consumer
 * never has to know which file a name lives in.
 *
 * ⚠️ THIS PARAGRAPH USED TO CLAIM `appListingGrid.ts` DERIVES THE STORE'S FOUR-COLUMN
 * THRESHOLD FROM {@link appsRailChromeWidth}. IT DOES NOT. `appListingGrid.ts` imports
 * exactly one module — `~/components/Apps/appsPageWidths` (`APPS_CONTAINER_GUTTER`,
 * `APPS_PAGE_CONTAINER_WIDTH`) — and never reads anything from this file. The rail's cost
 * IS an input to how that ladder was chosen (see `APPS_RAIL_MIN_VIEWPORT` below), but that
 * is provenance recorded in prose, not a code dependency. `appsRailChromeWidth` in fact has
 * no non-test consumers at all today: the layout applies the width inline, the stylesheet
 * only names the function in a comment, and every live call site is a geometry test.
 * Keeping the function is still right — it is the one place "292 open / 88 collapsed" is
 * arithmetic rather than a literal, and the tests that assert the rendered rect read it —
 * but do not reason about this module's dependents from the sentence that was here.
 */

/** The open rail's width, px. */
export const APPS_RAIL_WIDTH = 260;

/** The collapsed, icon-only rail's width, px. */
export const APPS_RAIL_COLLAPSED_WIDTH = 56;

/**
 * The gap between the rail and the page body, px.
 *
 * 32 because the rail panel paints 8px into this gap (see `.railSurface`), so the gap a
 * reader sees is 24px, in line with the collections and hubs sidebars. It is deliberately
 * NOT `SUBNAV_STICKY_GAP` (16, the rail's distance from the subnav above it): the side
 * gap is the one the design asked to widen, and the sticky offset is not.
 * `__tests__/appsRailGeometry.test.ts` pins the value against `.railRow`'s `gap` in the
 * stylesheet.
 */
export const APPS_RAIL_GAP = 32;

/**
 * The narrowest VIEWPORT at which the rail renders at all. Below this the sections move
 * into a `Drawer` opened from the page header band (the `CollectionsLayout` mobile
 * pattern).
 *
 * 🔴 IT IS EXPRESSED IN CSS, NOT IN A MEDIA-QUERY HOOK, AND THAT IS A HYDRATION
 * DECISION RATHER THAN A STYLE PREFERENCE. `window.matchMedia` has no server-side
 * answer, so a hook-driven swap renders the drawer shape on the server and the rail
 * shape on a desktop's first client paint — a mismatch of exactly the kind that once
 * bailed hydration of the entire `/apps` root and left every page inert. Both shapes are
 * rendered unconditionally and the browser picks one with a `@media` rule in
 * `AppsPageLayout.module.scss`, so the server HTML and the first client render are
 * byte-identical and there is no flash on either side.
 *
 * 🔴 THE NUMBER IS PINNED AGAINST THE STYLESHEET in
 * `__tests__/appsRailGeometry.test.ts` — the same seam guard `appListingGrid.test.ts`
 * puts on the store grid's container queries, and for the same reason: a TypeScript
 * constant nothing at runtime reads is exactly the thing that silently stops describing
 * the CSS.
 *
 * WHERE 1300 CAME FROM, and the caveat. It was chosen so a COLLAPSED rail still leaves
 * room for the store's four-column rung AS IT WAS BEFORE THIS CHANGE: at viewport 1280
 * with a 10px reserved scrollbar the grid is `1280 − 10 − 32 − 72 = 1166` against a
 * four-column rung of 1168 — two pixels short — while 1300 gives 1186 and clears it.
 * ⚠️ THAT DERIVATION IS NO LONGER LOAD-BEARING AND IS KEPT ONLY AS PROVENANCE. The
 * ladder re-tune shipped alongside the rail moves the four-column rung to 2242 of grid,
 * which no viewport near 1300 reaches with or without a rail — so the ladder no longer
 * discriminates between 1280 and 1300 at all, and 1300 now stands purely on "is there
 * room for 292px of chrome plus a usable body". Do not re-derive the old arithmetic as
 * if it still decided this number.
 */
export const APPS_RAIL_MIN_VIEWPORT = 1300;

/**
 * The width a scroll container reserves for a THIN scrollbar on the platforms that
 * reserve one at all (Windows / Linux Chrome + Firefox), px.
 *
 * 🔴 NOT A STYLE CONSTANT — a DERIVATION INPUT, and the reason the store ladder's
 * four-column rung is 2242 rather than 2252. `/apps` renders inside `.scroll-area`,
 * which `src/styles/globals.css` gives `scrollbar-width: thin` while
 * `html, body { overflow: hidden }` removes the document scroll — so the apps
 * `Container` sits inside a scrollbar-consuming box and the grid is
 * `viewport − scrollbar − APPS_CONTAINER_GUTTER − rail`. macOS overlay scrollbars and
 * touch reserve nothing, which is the LOOSER case: it yields 10px MORE grid, so a rung
 * placed for the reserving platform fires on both. Placing it for the non-reserving one
 * would have left Windows 10px short of its own desktop width.
 */
export const APPS_RESERVED_SCROLLBAR = 10;

/**
 * The horizontal chrome the rail costs the page body: the rail itself plus its gap.
 *
 * ONE function, several callers (the layout, the store ladder, every geometry test), so
 * "292 open / 88 collapsed" is arithmetic rather than literals that can drift.
 */
export function appsRailChromeWidth(collapsed: boolean): number {
  return (collapsed ? APPS_RAIL_COLLAPSED_WIDTH : APPS_RAIL_WIDTH) + APPS_RAIL_GAP;
}

/** The persisted rail state. A closed set, so an unknown value can only mean "open". */
export type AppsRailState = 'open' | 'collapsed';

/**
 * 🔴 OPEN BY DEFAULT, AND THE DEFAULT IS ALSO THE PARSE FAILURE MODE. Anything that is
 * not the exact literal `'collapsed'` resolves to `'open'`, so a truncated cookie, a
 * stale value from an older spelling, or a `localStorage` entry written by a different
 * feature can only ever fail OPEN — a visible navigation — rather than hiding the nav.
 */
export const APPS_RAIL_DEFAULT_STATE: AppsRailState = 'open';

/**
 * The cookie the SERVER reads to render the right first paint. Re-exported from
 * `~/shared/utils/cookies`, which owns the literal — see the note there for why the name
 * lives at the bottom of the dependency graph rather than here.
 */
export { APPS_RAIL_COOKIE };

/** A year, in seconds — the rail's cookie lifetime. */
export const APPS_RAIL_COOKIE_MAX_AGE = 60 * 60 * 24 * 365;

/** Narrow an arbitrary stored value to {@link AppsRailState}. Never throws. */
export function parseAppsRailState(value: string | undefined | null): AppsRailState {
  return value === 'collapsed' ? 'collapsed' : APPS_RAIL_DEFAULT_STATE;
}

/**
 * 🔴 THERE IS DELIBERATELY NO `readAppsRailCookie` HERE, AND ITS ABSENCE IS THE POINT.
 *
 * This module carried one: a hand-rolled `Cookie:`-header split, with eight assertions
 * covering multi-cookie headers, odd spacing and the `not-apps-rail=` substring trap. It
 * had ZERO production callers. The parser the server actually runs is the zod
 * `cookiesSchema` in `~/shared/utils/cookies`, reached from `_app`'s `getInitialProps`
 * via `cookies-next`'s `getCookies` — so the tests were exercising a second
 * implementation of a rule that already had one, and reading as coverage of the SSR seed
 * while covering none of it.
 *
 * That is the "one rule, one place" failure in its most flattering disguise: the dead
 * parser was more thoroughly tested than the live one. Deleted, and the cookie
 * assertions moved onto `parseCookies` itself in `__tests__/appsRailGeometry.test.ts`.
 * Do not reintroduce a second parser to make the seed testable — `parseCookies` is a
 * plain function and is node-importable.
 */
